import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { styleAPI, categoryAPI } from "../../lib/api";
import { useLanguage } from "../../context/LanguageContext";
import UserLayout from "../../components/UserLayout";
import ImageUploader from "../../components/ImageUploader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const AdminStyles = () => {
    const { t } = useLanguage();
    const [styles, setStyles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        prompt: "",
        thumbnail_url: "",
        category_id: "",
        tokens: 5,
        is_active: true
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [styleRes, catRes] = await Promise.all([
                styleAPI.getAll(),
                categoryAPI.getAll()
            ]);
            setStyles(styleRes.data);
            setCategories(catRes.data);
        } catch (error) {
            toast.error(t('loading') + " error");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.thumbnail_url) {
            toast.error(t('upload_thumbnail'));
            return;
        }
        
        if (!formData.category_id) {
            toast.error(t('select_category_first'));
            return;
        }
        
        setSaving(true);

        try {
            if (editing) {
                await styleAPI.update(editing, formData);
            } else {
                await styleAPI.create(formData);
            }
            toast.success(t('style_saved'));
            setDialogOpen(false);
            fetchData();
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error");
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (style) => {
        setEditing(style.id);
        setFormData({
            name: style.name,
            prompt: style.prompt,
            thumbnail_url: style.thumbnail_url,
            category_id: style.category_id,
            tokens: style.tokens || 5,
            is_active: style.is_active
        });
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('delete_confirm'))) return;
        
        try {
            await styleAPI.delete(id);
            toast.success(t('style_deleted'));
            fetchData();
        } catch (error) {
            toast.error("Error");
        }
    };

    const resetForm = () => {
        setEditing(null);
        setFormData({ name: "", prompt: "", thumbnail_url: "", category_id: "", tokens: 5, is_active: true });
    };

    const getCategoryName = (id) => categories.find(c => c.id === id)?.name || "-";

    return (
        <UserLayout>
            <div className="space-y-6" data-testid="admin-styles">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-gray-900">{t('styles_title')}</h1>
                        <p className="text-gray-600">{t('styles_subtitle')}</p>
                    </div>
                    <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="rounded-xl" data-testid="add-style-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('add_style')}
                    </Button>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('preview')}</TableHead>
                                <TableHead>{t('name')}</TableHead>
                                <TableHead>{t('category')}</TableHead>
                                <TableHead>{t('tokens')}</TableHead>
                                <TableHead>{t('prompt')}</TableHead>
                                <TableHead>{t('status')}</TableHead>
                                <TableHead className="text-right">{t('actions')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                                    </TableCell>
                                </TableRow>
                            ) : styles.length > 0 ? (
                                styles.map((style) => (
                                    <TableRow key={style.id}>
                                        <TableCell>
                                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                                                <img src={style.thumbnail_url} alt={style.name} className="w-full h-full object-cover" />
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-medium">{style.name}</TableCell>
                                        <TableCell>{getCategoryName(style.category_id)}</TableCell>
                                        <TableCell>
                                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                                                {style.tokens || 5}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-gray-500 max-w-xs truncate">{style.prompt}</TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${style.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                {style.is_active ? t('active') : t('inactive')}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button size="icon" variant="ghost" onClick={() => handleEdit(style)} data-testid={`edit-style-${style.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDelete(style.id)} data-testid={`delete-style-${style.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                                        No data
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>{editing ? t('edit_style') : t('add_new_style')}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label>{t('style_name')}</Label>
                                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder={t('style_example')} data-testid="style-name-input" />
                            </div>
                            <div className="space-y-2">
                                <Label>{t('category')}</Label>
                                <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                                    <SelectTrigger data-testid="style-category-select">
                                        <SelectValue placeholder={t('select_category')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map(cat => (
                                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>{t('token_cost')}</Label>
                                <Input 
                                    type="number" 
                                    value={formData.tokens} 
                                    onChange={(e) => setFormData({ ...formData, tokens: parseInt(e.target.value) || 0 })} 
                                    min={1}
                                    required 
                                    data-testid="style-tokens-input" 
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>{t('ai_prompt')}</Label>
                                <Textarea value={formData.prompt} onChange={(e) => setFormData({ ...formData, prompt: e.target.value })} required placeholder="Describe the pose/style..." data-testid="style-prompt-input" />
                            </div>
                            <div className="space-y-2">
                                <Label>{t('thumbnail')}</Label>
                                <ImageUploader
                                    value={formData.thumbnail_url}
                                    onChange={(url) => setFormData({ ...formData, thumbnail_url: url })}
                                    label="style-thumbnail"
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <Label>{t('status')} {t('active')}</Label>
                                <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
                                <Button type="submit" disabled={saving} data-testid="save-style-btn">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                    {saving ? t('loading') : t('save')}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </UserLayout>
    );
};

export default AdminStyles;
